import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Document } from '../../types/Document'; // Adjust path as needed
import { API_URL } from '@/lib/config';
import { documentTypeOptions } from '../dashboard/DocumentTypeDropdown';

interface DocumentHeaderProps {
  document: Document | null;
  editingTitle: boolean;
  newTitle: string;
  setNewTitle: (title: string) => void;
  savingTitle: boolean;
  handleSaveTitle: () => void;
  cancelTitleEdit: () => void;
  setEditingTitle: (editing: boolean) => void;
  isProcessing: boolean;
  handleProcessAction: () => void; // Combined processing action
  id: string | undefined; // document id
  processingResults?: any; // Processing results JSON to download
}

const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  document,
  editingTitle,
  newTitle,
  setNewTitle,
  savingTitle,
  handleSaveTitle,
  cancelTitleEdit,
  setEditingTitle,
  isProcessing,
  handleProcessAction,
  id,
  processingResults,
}) => {
  const navigate = useNavigate();

  if (!document) return null; // Should not happen if used correctly

  return (
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start flex-grow min-w-0">
          <button
            onClick={() => navigate('/dashboard')}
            className="mr-4 text-gray-400 hover:text-gray-500 flex-shrink-0"
            aria-label="Back to Dashboard"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
          </button>
          {/* Document type badge with icon */}
          {document.documentType && (() => {
            const typeOption = documentTypeOptions.find(opt => opt.id === document.documentType?.toLowerCase());
            return (
              <span className="mr-3 px-2.5 py-1 inline-flex items-center text-xs leading-5 font-medium rounded-md bg-gray-100 text-gray-600 border border-gray-200 flex-shrink-0">
                <span className="mr-1.5 [&>svg]:w-4 [&>svg]:h-4 [&>svg]:text-gray-500">
                  {typeOption?.icon}
                </span>
                {typeOption?.name || document.documentType}
              </span>
            );
          })()}
          {editingTitle ? (
            <div className="flex-grow min-w-0">
              <div className="flex items-start">
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="text-xl font-semibold text-gray-900 border border-blue-300 rounded px-2 py-1 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    else if (e.key === 'Escape') cancelTitleEdit();
                  }}
                />
                <div className="flex space-x-2 flex-shrink-0">
                  <button
                    onClick={handleSaveTitle}
                    disabled={savingTitle}
                    className={`p-1 rounded text-white ${savingTitle ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                    title="Save title"
                    aria-label="Save title"
                  >
                    {savingTitle ? (
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                    )}
                  </button>
                  <button
                    onClick={cancelTitleEdit}
                    className="p-1 rounded bg-gray-500 text-white hover:bg-gray-600"
                    title="Cancel editing title"
                    aria-label="Cancel editing title"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-grow min-w-0">
              <div className="flex items-start">
                <h1 className="text-xl font-semibold text-gray-900 break-words pr-2" title={document.originalName}>
                  {document.originalName}
                </h1>
                <button
                  onClick={() => {
                    setEditingTitle(true);
                  }}
                  className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 flex-shrink-0"
                  title="Edit title"
                  aria-label="Edit title"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4 flex-shrink-0">
          {document.processingStatus && (
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${ 
              document.processingStatus === 'done' ? 'bg-green-100 text-green-800 border-green-300'
                : document.processingStatus === 'error' ? 'bg-red-100 text-red-800 border-red-300'
                : document.processingStatus === 'not_processed' ? 'bg-gray-100 text-gray-800 border-gray-300'
                : 'bg-blue-100 text-blue-800 border-blue-300'
            }`}>{
              document.processingStatus === 'done' ? 'Processed' 
                : document.processingStatus === 'error' ? 'Processing Failed' 
                : document.processingStatus === 'not_processed' ? 'Not Processed' 
                : 'Processing...'}
            </span>
          )}
          <button
            onClick={() => {
              if (processingResults) {
                // Download the processing results JSON
                const jsonString = JSON.stringify(processingResults, null, 2);
                const blob = new Blob([jsonString], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = window.document.createElement('a');
                link.href = url;
                link.download = `${document?.originalName || 'document'}_results.json`;
                window.document.body.appendChild(link);
                link.click();
                window.document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } else if (document?.id) {
                // Fallback to original document if no processing results
                const url = `${API_URL}/documents/${document.id}/content`;
                window.open(url, '_blank');
              }
            }}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 whitespace-nowrap"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
            Download
          </button>
          {(!document.processingStatus || 
            document.processingStatus === 'not_processed' || 
            document.processingStatus === 'error') && id && (
            <button
              onClick={handleProcessAction}
              disabled={isProcessing}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white whitespace-nowrap ${ 
                isProcessing 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  {document.processingStatus === 'error' ? 'Retry Processing' : 'Process Document'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentHeader; 